/**
 * OpenBoardFilterBar — the chip row above the open board, plus the result count and sort control.
 *
 * Each chip is a summary AND a shortcut, and every chip opens the sheet *on its own section* — tap
 * "Any rank" and RANK is what you land on. Three chips that all opened the same undifferentiated
 * sheet made the row feel decorative; the section a chip asks for is what makes it a control.
 *
 * specs/open-challenges AC-17.
 */

import React, { useMemo } from 'react';
import {
  View, Text, Pressable, ScrollView, Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { ListFilter, ChevronDown, ChevronsUpDown } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets';
import {
  OPEN_BOARD_AMOUNT_OPTIONS,
  OPEN_BOARD_RANK_OPTIONS,
  OPEN_BOARD_SIZE_OPTIONS,
  OPEN_BOARD_SORT_OPTIONS,
  OPEN_BOARD_TEST_IDS,
} from '../../../src/challenges/constants/openBoard.constants';
import { countActiveFilters } from '../../../src/challenges/helpers/openBoardBrowse.helper';
import type {
  OpenBoardBrowseState,
  OpenBoardFilterSection,
} from '../../../src/challenges/types/openBoard.types';
import { getOpenBoardFilterBarStyles } from './OpenBoardFilterBar.styles';

interface OpenBoardFilterBarProps {
  browse: OpenBoardBrowseState;
  resultCount: number;
  /** `null` = the funnel button, which opens the sheet without singling out a section. */
  onOpenSheet: (section: OpenBoardFilterSection | null) => void;
  onChange: (next: OpenBoardBrowseState) => void;
}

function rankChipLabel(browse: OpenBoardBrowseState): string {
  const match = OPEN_BOARD_RANK_OPTIONS.find((o) => o.rankFloor === browse.rankFloor);
  return match?.label ?? OPEN_BOARD_RANK_OPTIONS[0].label;
}

function amountChipLabel(browse: OpenBoardBrowseState): string {
  const match = OPEN_BOARD_AMOUNT_OPTIONS.find(
    (o) => o.minStake === browse.minStake && o.maxStake === browse.maxStake
  );
  return match?.label ?? OPEN_BOARD_AMOUNT_OPTIONS[0].label;
}

export const OpenBoardFilterBar: React.FC<OpenBoardFilterBarProps> = ({
  browse,
  resultCount,
  onOpenSheet,
  onChange,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getOpenBoardFilterBarStyles(theme), [theme]);

  const activeCount = countActiveFilters(browse);
  const rankActive = browse.rankFloor != null;
  const amountActive = browse.minStake != null || browse.maxStake != null;
  const sizeActive = browse.size !== 'all';
  const bigSlateOnly = browse.size === 'big';

  const cycleSort = () => {
    const i = OPEN_BOARD_SORT_OPTIONS.findIndex((o) => o.id === browse.sort);
    const next = OPEN_BOARD_SORT_OPTIONS[(i + 1) % OPEN_BOARD_SORT_OPTIONS.length];
    onChange({ ...browse, sort: next.id });
  };

  const sortLabel = (OPEN_BOARD_SORT_OPTIONS.find((o) => o.id === browse.sort)
    ?? OPEN_BOARD_SORT_OPTIONS[0]).label;
  const sizeLabel = (OPEN_BOARD_SIZE_OPTIONS.find((o) => o.id === browse.size)
    ?? OPEN_BOARD_SIZE_OPTIONS[0]).label;

  return (
    <View>
      <View style={styles.bar}>
        <Pressable
          onPress={() => onOpenSheet(null)}
          testID={OPEN_BOARD_TEST_IDS.filterButton}
          accessibilityRole="button"
          accessibilityLabel={
            activeCount > 0 ? `Filters, ${activeCount} active` : 'Filters'
          }
          style={({ pressed }) => [
            styles.filterButton,
            activeCount > 0 && styles.filterButtonActive,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.filterButtonPressed,
          ]}
        >
          <ListFilter
            size={18}
            color={activeCount > 0 ? theme.primaryLight : theme.gray[600]}
            strokeWidth={2}
          />
          {activeCount > 0 && (
            <View style={styles.filterBadge} testID={OPEN_BOARD_TEST_IDS.filterBadge}>
              <Text style={styles.filterBadgeText}>{activeCount}</Text>
            </View>
          )}
        </Pressable>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipScroller}
          contentContainerStyle={styles.chipRow}
        >
          <Pressable
            onPress={() => onOpenSheet('rank')}
            testID={OPEN_BOARD_TEST_IDS.rankChip}
            accessibilityRole="button"
            accessibilityLabel={`Rank: ${rankChipLabel(browse)}`}
            style={({ pressed }) => [
              styles.chip,
              rankActive && styles.chipActive,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.chipPressed,
            ]}
          >
            <Text style={[styles.chipLabel, rankActive && styles.chipLabelActive]} numberOfLines={1}>
              {rankChipLabel(browse)}
            </Text>
            <ChevronDown size={12} color={rankActive ? theme.primaryLight : theme.gray[500]} strokeWidth={2.6} />
          </Pressable>

          <Pressable
            onPress={() => onOpenSheet('amount')}
            testID={OPEN_BOARD_TEST_IDS.amountChip}
            accessibilityRole="button"
            accessibilityLabel={`Entry amount: ${amountChipLabel(browse)}`}
            style={({ pressed }) => [
              styles.chip,
              amountActive && styles.chipActive,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.chipPressed,
            ]}
          >
            <Text style={[styles.chipLabel, amountActive && styles.chipLabelActive]} numberOfLines={1}>
              {amountChipLabel(browse)}
            </Text>
            <ChevronDown size={12} color={amountActive ? theme.primaryLight : theme.gray[500]} strokeWidth={2.6} />
          </Pressable>

          <Pressable
            onPress={() => onOpenSheet('size')}
            testID={OPEN_BOARD_TEST_IDS.sizeChip}
            accessibilityRole="button"
            accessibilityLabel={`Challenge size: ${sizeLabel}`}
            style={({ pressed }) => [
              styles.chip,
              sizeActive && (bigSlateOnly ? styles.chipAccent : styles.chipActive),
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.chipPressed,
            ]}
          >
            {bigSlateOnly && (
              <Image
                source={{ uri: getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON) }}
                style={styles.chipCrown}
                contentFit="contain"
                tintColor={theme.accent}
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            )}
            <Text
              style={[
                styles.chipLabel,
                sizeActive && (bigSlateOnly ? styles.chipLabelAccent : styles.chipLabelActive),
              ]}
              numberOfLines={1}
            >
              {sizeLabel}
            </Text>
            <ChevronDown
              size={12}
              color={sizeActive && !bigSlateOnly ? theme.primaryLight : theme.gray[500]}
              strokeWidth={2.6}
            />
          </Pressable>
        </ScrollView>
      </View>

      <View style={styles.resultRow}>
        <Text style={styles.resultText} testID={OPEN_BOARD_TEST_IDS.resultLine} numberOfLines={1}>
          {resultCount} {resultCount === 1 ? 'open challenge' : 'open challenges'}
        </Text>
        <Pressable
          onPress={cycleSort}
          testID={OPEN_BOARD_TEST_IDS.sortControl}
          accessibilityRole="button"
          accessibilityLabel={`Sort: ${sortLabel}. Tap to change.`}
          style={({ pressed }) => [styles.sortControl, pressed && styles.sortPressed]}
        >
          <ChevronsUpDown size={13} color={theme.primary} strokeWidth={2.2} />
          <Text style={styles.sortLabel}>{sortLabel}</Text>
        </Pressable>
      </View>
    </View>
  );
};
